import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import API_BASE from '../config';
import { authHeaders, clearEditPassword } from '../services/auth';
import './ReactionTypes.css';

const ReactionTypes = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});       // { originalName: newName }
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [filter, setFilter] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(false);

  const load = async () => {
    setLoading(true);
    const resp = await fetch(`${API_BASE}/reaction-types`);
    const data = await resp.json();
    setTypes(data);
    setEdits({});
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleChange = (original, value) => {
    setEdits(prev => ({ ...prev, [original]: value }));
  };

  const changedTypes = types.filter(t => {
    const edited = edits[t.reaction_type];
    return edited !== undefined && edited.trim() !== '' && edited.trim() !== t.reaction_type;
  });

  const saveAll = async () => {
    if (changedTypes.length === 0) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      for (const t of changedTypes) {
        const resp = await fetch(`${API_BASE}/reaction-types/rename`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ from_type: t.reaction_type, to_type: edits[t.reaction_type].trim() }),
        });
        if (resp.status === 401) { clearEditPassword(); throw new Error('Wrong password'); }
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Rename failed');
      }
      await load();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addNewType = async () => {
    if (!newTypeName.trim()) return;
    setAddError(null);
    try {
      const resp = await fetch(`${API_BASE}/reaction-types`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name: newTypeName.trim() }),
      });
      if (resp.status === 401) { clearEditPassword(); throw new Error('Wrong password'); }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Add failed');
      setNewTypeName('');
      setAddSuccess(true);
      setTimeout(() => setAddSuccess(false), 2000);
      await load();
    } catch (err) {
      setAddError(err.message);
    }
  };

  const filtered = filter.trim()
    ? types.filter(t => t.reaction_type?.toLowerCase().includes(filter.toLowerCase()))
    : types;

  // All current names (after pending edits) for the datalist
  const allNames = Array.from(new Set(
    types.map(t => (edits[t.reaction_type] ?? t.reaction_type) || '(none)')
  )).sort();

  if (loading) return <div className="rt-loading">Loading…</div>;

  return (
    <div className="rt-container">
      <Link to="/edit" className="rt-back">← Back to Edit</Link>
      <div className="rt-header">
        <h1>Reaction Types</h1>
        <span className="rt-count">{types.length} types · {types.reduce((s, t) => s + t.count, 0)} reactions</span>
      </div>
      <p className="rt-hint">
        Edit names inline to rename or merge types. Typing the same name for two rows will merge them when you save.
      </p>

      <div className="rt-toolbar">
        <input
          className="rt-filter"
          placeholder="Filter types…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        {changedTypes.length > 0 && (
          <button className="rt-save-btn" onClick={saveAll} disabled={saving}>
            {saving ? 'Saving…' : `Save ${changedTypes.length} change${changedTypes.length !== 1 ? 's' : ''}`}
          </button>
        )}
        {saveSuccess && <span className="rt-success">Saved.</span>}
        {saveError && <span className="rt-error">{saveError}</span>}
      </div>

      <div className="rt-add-section">
        <input
          className="rt-add-input"
          value={newTypeName}
          onChange={e => setNewTypeName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addNewType()}
          placeholder="New reaction type name…"
        />
        <button className="rt-add-btn" onClick={addNewType} disabled={!newTypeName.trim()}>
          Add Type
        </button>
        {addSuccess && <span className="rt-success">Added.</span>}
        {addError && <span className="rt-error">{addError}</span>}
      </div>

      <datalist id="type-names">
        {allNames.map(n => <option key={n} value={n} />)}
      </datalist>

      <table className="rt-table">
        <thead>
          <tr>
            <th>Reaction Type</th>
            <th className="rt-count-col">Reactions</th>
            <th>Rename to</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(t => {
            const original = t.reaction_type;
            const editVal = edits[original] ?? original ?? '';
            const changed = editVal.trim() !== (original ?? '');
            return (
              <tr key={original} className={changed ? 'rt-row-changed' : ''}>
                <td className="rt-original">{original || <em className="rt-none">(none)</em>}</td>
                <td className="rt-count-col">{t.count}</td>
                <td>
                  <input
                    className="rt-edit-input"
                    value={editVal}
                    list="type-names"
                    onChange={e => handleChange(original, e.target.value)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {filtered.length === 0 && (
        <div className="rt-empty">No types match "{filter}"</div>
      )}
    </div>
  );
};

export default ReactionTypes;
